import { useEffect, useState } from "react";
import { toast } from "sonner";
import { api, formatApiError, formatDate } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Mail, MailOpen, Trash2, Inbox, Zap, AlertCircle } from "lucide-react";

export default function OwnerMessages() {
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all"); // all | unread | activation
  const [extendingId, setExtendingId] = useState(null);

  const load = async () => {
    setLoading(true);
    try { const { data } = await api.get("/owner/messages"); setMessages(data); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const toggleRead = async (m) => {
    try {
      if (m.read) await api.put(`/owner/messages/${m.id}/unread`);
      else await api.put(`/owner/messages/${m.id}/read`);
      load();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  const remove = async (id) => {
    try { await api.delete(`/owner/messages/${id}`); toast.success("Eliminado"); load(); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  const extend30 = async (m) => {
    setExtendingId(m.id);
    try {
      await api.post(`/owner/admins/${m.admin_id}/extend`, { days: 30 });
      // Mark message as read once handled
      if (!m.read) {
        try { await api.put(`/owner/messages/${m.id}/read`); } catch { /* noop */ }
      }
      toast.success(`Suscripción de ${m.admin_name} extendida 30 días`);
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "No se pudo extender");
    } finally {
      setExtendingId(null);
    }
  };

  const filteredMessages = (() => {
    if (filter === "unread") return messages.filter(m => !m.read);
    if (filter === "activation") return messages.filter(m => m.kind === "activation_request");
    return messages;
  })();
  const visible = filteredMessages;
  const unreadCount = messages.filter(m => !m.read).length;
  const activationCount = messages.filter(m => m.kind === "activation_request" && !m.read).length;

  return (
    <div className="animate-fade-up" data-testid="owner-messages-page">
      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4 mb-8">
        <div>
          <p className="text-xs uppercase tracking-[0.3em] text-[#7248ae] mb-2">Bandeja</p>
          <h1 className="font-display text-4xl sm:text-5xl font-black text-[#1a0f2e] tracking-tight">Mensajes</h1>
          <p className="text-[#7248ae] mt-2">Todos los mensajes enviados por administradores desde la app.</p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <button onClick={() => setFilter("all")} className={`px-3 py-1.5 rounded-full text-xs font-medium ${filter==="all" ? "bg-[#3e166c] text-white" : "text-[#3e166c] hover:bg-[#3e166c]/10"}`} data-testid="filter-all">
            Todos ({messages.length})
          </button>
          <button onClick={() => setFilter("unread")} className={`px-3 py-1.5 rounded-full text-xs font-medium ${filter==="unread" ? "bg-[#3e166c] text-white" : "text-[#3e166c] hover:bg-[#3e166c]/10"}`} data-testid="filter-unread">
            Sin leer ({unreadCount})
          </button>
          <button
            onClick={() => setFilter("activation")}
            className={`px-3 py-1.5 rounded-full text-xs font-medium inline-flex items-center gap-1 ${filter==="activation" ? "bg-[#e11d48] text-white" : "text-[#e11d48] hover:bg-[#e11d48]/10"}`}
            data-testid="filter-activation"
          >
            <Zap className="h-3 w-3" />
            Reactivaciones {activationCount > 0 && <span className="ml-1">({activationCount})</span>}
          </button>
        </div>
      </div>

      {loading ? (
        <p className="text-[#7248ae]">Cargando…</p>
      ) : visible.length === 0 ? (
        <Card className="p-12 text-center border-dashed border-[#b182ec]/40">
          <Inbox className="h-10 w-10 text-[#b182ec] mx-auto mb-3" />
          <p className="text-[#7248ae]">No hay mensajes {filter === "unread" ? "sin leer" : "todavía"}.</p>
        </Card>
      ) : (
        <div className="space-y-3" data-testid="messages-list">
          {visible.map((m, i) => {
            const isActivation = m.kind === "activation_request";
            return (
            <Card
              key={m.id}
              className={`p-5 border rounded-2xl card-glow transition-all animate-fade-up stagger-${(i%6)+1} ${
                isActivation && !m.read ? "border-[#e11d48]/60 bg-[#e11d48]/5 ring-1 ring-[#e11d48]/30"
                : m.read ? "border-[#b182ec]/30 bg-white"
                : "border-[#3e166c]/40 bg-[#3e166c]/5"
              }`}
              data-testid={`message-${m.id}`}
            >
              <div className="flex items-start justify-between gap-4 mb-3">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1 flex-wrap">
                    {!m.read && <span className="w-2 h-2 rounded-full bg-[#3e166c]" />}
                    {isActivation && (
                      <Badge className="bg-[#e11d48] hover:bg-[#e11d48]/90 inline-flex items-center gap-1" data-testid={`badge-activation-${m.id}`}>
                        <AlertCircle className="h-3 w-3" />
                        Solicitud de reactivación
                      </Badge>
                    )}
                    <h3 className="font-display text-lg font-bold text-[#1a0f2e] truncate">{m.subject}</h3>
                  </div>
                  <p className="text-sm text-[#7248ae]">
                    <strong className="text-[#1a0f2e]">{m.admin_name}</strong> · {m.restaurant_name} · {formatDate(m.sent_at)}
                  </p>
                  <p className="text-xs text-[#7248ae]">{m.admin_email}</p>
                </div>
                <Badge className={m.read ? "bg-[#7248ae]" : "bg-[#3e166c]"}>{m.read ? "Leído" : "Nuevo"}</Badge>
              </div>
              <p className="text-[#1a0f2e] whitespace-pre-line leading-relaxed mb-4">{m.message}</p>
              <div className="flex gap-2 flex-wrap">
                {isActivation && (
                  <Button
                    size="sm"
                    onClick={() => extend30(m)}
                    disabled={extendingId === m.id}
                    className="bg-[#10b981] hover:bg-[#065f46] text-white"
                    data-testid={`extend-30-${m.id}`}
                  >
                    <Zap className="h-4 w-4 mr-1" />
                    {extendingId === m.id ? "Extendiendo…" : "Extender 30 días"}
                  </Button>
                )}
                <Button size="sm" variant="outline" onClick={() => toggleRead(m)} className="border-[#b182ec]/50" data-testid={`toggle-read-${m.id}`}>
                  {m.read ? (<><Mail className="h-4 w-4 mr-1" />Marcar no leído</>) : (<><MailOpen className="h-4 w-4 mr-1" />Marcar leído</>)}
                </Button>
                <Button size="sm" variant="outline" asChild className="border-[#b182ec]/50" data-testid={`reply-${m.id}`}>
                  <a href={`mailto:${m.admin_email}?subject=Re: ${encodeURIComponent(m.subject)}`}>Responder por correo</a>
                </Button>
                <Button size="sm" variant="outline" onClick={() => remove(m.id)} className="border-[#e11d48]/40 text-[#e11d48] hover:bg-[#e11d48]/10 ml-auto" data-testid={`delete-msg-${m.id}`}>
                  <Trash2 className="h-4 w-4" />
                </Button>
              </div>
            </Card>
          );
          })}
        </div>
      )}
    </div>
  );
}
